import { MapComponent, MapType } from '../map/map.component';
import { MarwisLegendComponent } from './marwis-legend.component';

import { AuthService } from '../auth/auth.service';
import { RadarDtg } from '../radar/radar.service';
import { LanguageService } from '../shared/services/language.service';
import { MarwisService } from './marwis.service';

import { DatexPipe } from '../shared/datex.pipe';
import { ContrastColorPipe } from './pipes/contrast-color.pipe';
import { GetColorPipe } from './pipes/get-color.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  ChangeContext,
  LabelType,
  NgxSliderModule,
  Options,
} from '@angular-slider/ngx-slider';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { TippyDirective } from '@ngneat/helipopper';
import { LngLatBounds } from 'mapbox-gl';
import moment from 'moment';
import { Moment } from 'moment';
import { Subscription, timer } from 'rxjs';
import { sliderBaseOptions } from 'src/constants';

export const MODULES = [
  CommonModule,
  MatSidenavModule,
  MatIconModule,
  MatButtonModule,
  ReactiveFormsModule,
  FormsModule,
  RouterModule,
  MatFormFieldModule,
  MatSelectModule,
  MatCardModule,
  MatTooltipModule,
  NgxSliderModule,
  MatDividerModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  MapComponent,
  MarwisLegendComponent,
];
export const PIPES = [DatexPipe, GetColorPipe, ContrastColorPipe];
export const DIRECTIVES = [WmIconDirective, TippyDirective];

@Component({
  selector: 'app-marwis',
  templateUrl: './marwis.component.html',
  styleUrls: ['./marwis.component.scss'],
  providers: [MarwisService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class MarwisComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  public marwisService = inject(MarwisService);
  public languageService = inject(LanguageService);

  mapType: typeof MapType = MapType;

  marwisStations = [101117, 131117];
  marwisCars = signal([]);
  vecicleNames = signal({});
  marwisId = signal<number>(undefined);
  carControl = new UntypedFormControl();
  timerangeControl = new UntypedFormControl();
  tourControl = new UntypedFormControl();

  animationSubscription: Subscription;
  sliderOptions = signal<Options>(sliderBaseOptions);

  geoJson = signal<any>(undefined);
  dtgs: Array<RadarDtg> = [];
  loading = true;
  carPosition = 0;
  sliderDate = model<number>();
  feature = signal<any>(undefined);
  marwisData: any;
  geoJsonPerTour = [];
  tourDates = signal([]);
  selectedTour = 0;
  selectedTimeRange = 'LATEST';
  monthDtg = {};
  tourMonths = signal([]);
  latestTourDate;
  latest: string;
  animationIsRunning = signal(false);
  speed = model<number>(60);

  animationSpeedOptions = signal<Options>({
    showTicksValues: true,
    stepsArray: [
      { value: 10, legend: 'langsam' },
      { value: 20 },
      { value: 40 },
      { value: 60 },
      { value: 80 },
      { value: 100, legend: 'schnell' },
    ],
  });

  bounds;
  allParameters = [
    'road_condition',
    'road_tt',
    'road_rh',
    'wft',
    'tt',
    'td',
    'rh',
  ];
  parameters = signal([]);
  selectedParameter = signal('road_condition');

  ngOnInit(): void {
    this.readUserSettings();
    this.getMarwisCars();
    this.getTourMonths();
    this.getMarwisData('LATEST');
  }

  ngOnDestroy(): void {
    this.animationIsRunning.set(false);
    this.stopTimer();
  }

  getMarwisCars(): void {
    const customerDocument = this.auth.getCustomerDocument();
    if (this.auth.isMaster() && !this.auth.getCustomerAlias()) {
      this.marwisCars.set([
        { id: 131117, name: 'Marwis 1' },
        { id: 101117, name: 'Marwis 2' },
      ]);
    } else {
      const vehicles = [];
      for (const marwisProduct of customerDocument.marwisProducts) {
        if (
          !this.auth.isProductActive('marwisProducts', marwisProduct.id, true)
        ) {
          continue;
        }
        for (const vehicle of marwisProduct.vehicles) {
          if (
            this.auth.isUserAllowedToAccessToProduct(
              marwisProduct.id,
              vehicle.id
            )
          ) {
            vehicles.push(vehicle);
          }
        }
      }
      this.marwisCars.set([...vehicles]);
    }
    for (const car of this.marwisCars()) {
      this.vecicleNames.update(vecicleNames => {
        return { ...vecicleNames, [car.id]: car.name };
      });
    }
    if (this.marwisCars().length > 0) {
      this.marwisId.set(this.marwisCars()[0].id);
      this.carControl.setValue(this.marwisId());
    }
  }

  splitMarwisDataIntoTours(marwisData): void {
    let tourStartDtg = null;
    let tourEndDtg = null;
    let lastDtg: Moment;
    let coords = [];
    const geoJsonPerTour = [];
    let geoJsonOfOneTour = this.getEmptyGeoJson();

    for (const marwis of this.marwisData) {
      const dataset = this.getMarwisDataset(marwis);
      if (!this.latestTourDate && this.selectedTimeRange === 'LATEST') {
        this.latestTourDate = dataset.dtg;
        this.getTourMonths();
      }
      if (!tourStartDtg) {
        tourStartDtg = dataset.dtg;
      }
      if (lastDtg && this.selectedTimeRange !== 'LATEST') {
        // || geoJsonOfOneTour.features.length > 1500) {
        if (
          Math.abs(lastDtg.diff(dataset.dtg, 'minutes')) > 10 ||
          geoJsonOfOneTour.features.length > 1500
        ) {
          //            ', Diff: ' + lastDtg.diff(dataset.dtg, 'seconds'));
          tourEndDtg = lastDtg.format('YYYY-MM-DDTHH:mm:ss');
          geoJsonPerTour.push({
            startDtg: tourStartDtg,
            endDtg: tourEndDtg,
            geoJson: geoJsonOfOneTour,
            bounds: [3.51175, 46.959255, 17.381118, 55.600819],
          });
          tourStartDtg = dataset.dtg;
          tourEndDtg = dataset.dtg;
          geoJsonOfOneTour = this.getEmptyGeoJson();
          coords = [];
        }
      }
      lastDtg = moment(dataset.dtg);
      coords.push([dataset['longitude'], dataset['latitude']]);
      geoJsonOfOneTour.features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [dataset['longitude'], dataset['latitude']],
        },
        properties: {
          dtg: dataset.dtg,
          longitude: dataset['longitude'],
          latitude: dataset['latitude'],
          rh: dataset.hasOwnProperty('rh') ? dataset['rh'] : null,
          road_condition: dataset.hasOwnProperty('road_condition')
            ? dataset['road_condition']
            : null,
          road_rh: dataset.hasOwnProperty('road_rh')
            ? dataset['road_rh']
            : null,
          road_tt: dataset.hasOwnProperty('road_tt')
            ? dataset['road_tt']
            : null,
          speed: dataset.hasOwnProperty('speed') ? dataset['speed'] : null,
          td: dataset.hasOwnProperty('td') ? dataset['td'] : null,
          tt: dataset.hasOwnProperty('tt') ? dataset['tt'] : null,
          wft: dataset.hasOwnProperty('wft') ? dataset['wft'] : null,
          color: 'red',
        },
      });
    }
    // last tour of timerange
    if (lastDtg) {
      // bounds
      const marwisBounds = coords.reduce(
        (bounds, coord) => {
          return bounds.extend(coord);
        },
        new LngLatBounds(coords[0], coords[0])
      );

      tourEndDtg = lastDtg.format('YYYY-MM-DDTHH:mm:ss');
      geoJsonPerTour.push({
        startDtg: tourStartDtg,
        endDtg: tourEndDtg,
        geoJson: geoJsonOfOneTour,
        bounds: marwisBounds,
      });
    }
    geoJsonPerTour.reverse();
    this.extractTourDates(geoJsonPerTour);
    this.geoJsonPerTour = geoJsonPerTour;
    this.selectedTour = 0;
    this.tourControl.setValue(0);
    if (geoJsonPerTour.length > 0) {
      this.geoJson.set({ ...geoJsonPerTour[0].geoJson });
      this.feature.set(
        this.geoJson().features.length > 0
          ? { ...this.geoJson().features[0] }
          : null
      );
      this.sliderDate.set(moment(this.feature().properties.dtg).clone().unix());
      this.setSliderOptions();
    }
  }

  extractTourDates(tourData: Array<any>): void {
    const tourDates = [];
    for (const tour of tourData) {
      tourDates.push({ startDtg: tour.startDtg, endDtg: tour.endDtg });
    }
    this.tourDates.set([...tourDates]);
  }

  getTourMonths(): void {
    const customerDocument = this.auth.getCustomerDocument();
    this.tourMonths.set(['LATEST']);
    this.timerangeControl.setValue('LATEST');
    if (this.auth.isMaster() && !this.auth.getCustomerAlias()) {
      const startDate = moment('2017-11-01');
      const endDate = moment(this.latestTourDate);
      for (
        let runDtg = endDate;
        runDtg >= startDate;
        runDtg = runDtg.subtract(1, 'month')
      ) {
        const tourMonth = runDtg.format('MMMM YYYY');
        this.tourMonths.update(tourMonths => {
          tourMonths.push(tourMonth);
          return [...tourMonths];
        });
        this.monthDtg[tourMonth] = runDtg.format('YYYY-MM-01');
      }
    } else {
      const marwisProducts = customerDocument['marwisProducts'];
      for (const product of marwisProducts) {
        if (this.auth.isProductActive('marwisProducts', product.id, true)) {
          const startDate = moment(product['contractStartDtg']);
          const latest = moment(this.latestTourDate);
          const endDate =
            !product['contractExpiryDtg'] ||
            moment(this.latestTourDate).isBefore(
              moment(product['contractExpiryDtg'])
            )
              ? latest
              : moment(product['contractExpiryDtg']);
          for (
            let runDtg = endDate;
            runDtg >= startDate;
            runDtg = runDtg.subtract(1, 'month')
          ) {
            const tourMonth = runDtg.format('MMMM YYYY');
            if (this.tourMonths().indexOf(tourMonth) < 0) {
              this.tourMonths.update(tourMonths => {
                tourMonths.push(tourMonth);
                return [...tourMonths];
              });
              this.monthDtg[tourMonth] = runDtg.format('YYYY-MM-01');
            }
          }
        }
      }
    }

    this.monthDtg['LATEST'] = this.latest;
  }

  getMarwisData(month: string): void {
    this.loading = true;
    this.geoJson.set({ ...this.getEmptyGeoJson() });
    this.carPosition = 0;
    this.feature.set(null);

    const monthParam = month === 'LATEST' ? null : this.monthDtg[month];

    this.marwisService.getMarwisData(this.marwisId(), monthParam).then(data => {
      this.marwisData = data;
      if (this.marwisData.length > 0) {
        this.extractParameters();
        this.splitMarwisDataIntoTours(this.marwisData);
      } else {
        this.geoJsonPerTour = [];
        this.selectedTour = 0;
        this.marwisData = null;
      }
      this.loading = false;
    });
  }

  getEmptyGeoJson(): any {
    return {
      type: 'FeatureCollection',
      crs: {
        type: 'name',
        properties: {
          name: 'EPSG:4326',
        },
      },
      features: [],
    };
  }

  extractParameters(): void {
    this.parameters.set([]);
    const dataset = this.getMarwisDataset(this.marwisData[0]);
    for (const parameter of this.allParameters) {
      if (dataset.hasOwnProperty(parameter)) {
        this.parameters.update(parameters => {
          parameters.push(parameter);
          return [...parameters];
        });
      }
    }
    if (this.parameters().indexOf(this.selectedParameter()) < 0) {
      this.selectedParameter.set(this.parameters()[0]);
    }
  }

  startTimer(): void {
    const marwisTimer = timer(0, 2500 / this.speed());
    this.animationSubscription = marwisTimer.subscribe(t => {
      this.carPosition++;
      if (this.carPosition >= this.geoJson().features.length) {
        this.carPosition = 0;
      }
      if (this.geoJson().features.length > this.carPosition) {
        this.feature.set({ ...this.geoJson().features[this.carPosition] });
        this.sliderDate.set(
          moment(this.feature().properties.dtg).clone().unix()
        );
      }
    });
  }

  stopTimer(): void {
    if (this.animationSubscription) {
      this.animationSubscription.unsubscribe();
    }
  }

  setSliderOptions(): void {
    const tickStep = Math.max(
      Math.floor(this.geoJson().features.length / 20),
      1
    );
    this.sliderOptions.set({
      showTicks: true,
      showTicksValues: true,
      stepsArray: this.geoJson().features.map((feature: any) => {
        const dtg = feature.properties.dtg;
        return {
          value: moment(dtg).unix(),
          legend:
            dtg === this.geoJson().features[0].properties.dtg ||
            dtg ===
              this.geoJson().features[this.geoJson().features.length - 1]
                .properties.dtg
              ? moment(dtg).format('HH:mm')
              : '',
        };
      }),
      ticksValuesTooltip: (step: number) => {
        const dtg = moment(step);
        return dtg.format('HH:mm:ss');
      },
      tickStep,
      getTickColor: (value: number): string => {
        return '#399DE6';
      },
      translate: (value: number, label: LabelType): string => {
        const dtg = moment.unix(value);
        if (label !== LabelType.TickValue) {
          const dtgFormat = label === LabelType.Floor ? 'HH:mm:ss' : 'HH:mm:ss';
          return dtg.format(dtgFormat);
        }
        return null;
        const stepDtg = moment(this.geoJson().features[value].dtg);
        return stepDtg.minutes() % 5 === 0 ? stepDtg.format('HH:mm') : '';
      },
    });
  }

  setDtgs(): void {
    this.dtgs = [];
    for (const feature of this.geoJson().features) {
      const dtg = moment(feature.properties.dtg);
      const dtgUtc = moment(dtg).utc();
      const timestamp = dtg.unix();
      this.dtgs.push({
        mapServer: null,
        slider: timestamp,
        info: dtg.format('dd. DD.MM.YYYY HH:mm'),
        visible: null,
        isFcst: null,
        isArchive: null,
        issue: null,
      });
    }
  }

  onStationChange(event): void {
    if (event.isUserInput) {
      this.marwisId.set(event.source.value);
      this.selectedTimeRange = 'LATEST';
      this.timerangeControl.setValue('LATEST');
      this.latestTourDate = null;
      this.getMarwisData(this.selectedTimeRange);
    }
  }

  onMonthChange(event): void {
    if (event.isUserInput) {
      const month = event.source.value;
      if (this.selectedTimeRange !== month) {
        this.selectedTimeRange = month;
        this.getMarwisData(month);
      }
    }
  }

  onTourChange(event): void {
    if (event.isUserInput) {
      if (this.selectedTour !== event.source.value) {
        this.geoJson.set({ ...this.getEmptyGeoJson() });
        this.carPosition = 0;
        this.selectedTour = event.source.value;
        this.geoJson.set({ ...this.geoJsonPerTour[this.selectedTour].geoJson });
        this.setSliderOptions();
        this.feature.set(
          this.geoJson().features.length > 0
            ? { ...this.geoJson().features[0] }
            : null
        );
        this.bounds = this.geoJsonPerTour[this.selectedTour].bounds;
        this.sliderDate.set(
          moment(this.feature().properties.dtg).clone().unix()
        );
      }
    }
  }

  onMoveCarPositionStart(): void {
    if (this.animationIsRunning()) {
      this.onPause();
    }
    this.carPosition = 0;
    this.feature.set({ ...this.geoJson().features[this.carPosition] });
  }

  onMoveCarPositionEnd(): void {
    if (this.animationIsRunning()) {
      this.onPause();
    }
    this.carPosition = this.geoJson().features.length - 1;
    this.feature.set({ ...this.geoJson().features[this.carPosition] });
  }

  onMoveCarPositionNext(): void {
    if (this.carPosition < this.geoJson().features.length - 1) {
      this.carPosition++;
      this.feature.set({ ...this.geoJson().features[this.carPosition] });
    }
  }

  onMoveCarPositionPrevious(): void {
    if (this.carPosition > 0) {
      this.carPosition--;
      this.feature.set({ ...this.geoJson().features[this.carPosition] });
    }
  }

  onPlay(): void {
    this.animationIsRunning.set(true);
    this.startTimer();
  }

  onPause(): void {
    this.animationIsRunning.set(false);
    this.stopTimer();
  }

  onSpeedChange(): void {
    if (this.animationIsRunning()) {
      this.onPause();
      this.onPlay();
    }
  }

  onSliderChange(event: ChangeContext): void {
    const dtg = moment.unix(event.value);
    for (let i = 0; i < this.geoJson().features.length; i++) {
      const feature = this.geoJson().features[i];
      const featureDtg = moment(feature.properties.dtg);
      if (dtg.isSame(featureDtg)) {
        this.feature.set({ ...feature });
        this.carPosition = i;
        break;
      }
    }
    // this.feature.set({...this.geoJson().features[this.carPosition]});
    //    this.setRadarTime(event.value);
    //    this.onPause();
  }

  private getMarwisDataset(data): any {
    const marwisDataset = {
      dtg: data.dtg,
    };
    for (const parameter of data.weatherparameters) {
      for (const key in parameter) {
        if (parameter.hasOwnProperty(key)) {
          marwisDataset[key] = parameter[key];
        }
      }
    }
    return marwisDataset;
  }

  onParameterSelect(parameter: string): void {
    this.selectedParameter.set(parameter);
  }

  onMapClick(event): void {
    if (
      event.hasOwnProperty('properties') &&
      event.properties.hasOwnProperty('dtg')
    ) {
      const dtg = event.properties.dtg;
      for (let i = 0; i < this.geoJson().features.length; i++) {
        if (dtg === this.geoJson().features[i].properties.dtg) {
          this.onPause();
          this.feature.set({ ...this.geoJson().features[i] });
          this.carPosition = i;
          this.sliderDate.set(moment(dtg).clone().unix());
        }
      }
    }
  }

  saveUserSettings(): void {
    const settings = { speed: this.speed() };
    localStorage.setItem('marwisSettings', JSON.stringify(settings));
  }

  readUserSettings(): void {
    const savedValue = localStorage.getItem('marwisSettings');
    if (savedValue) {
      const savedSettings = JSON.parse(savedValue);
      if (savedSettings.hasOwnProperty('speed')) {
        this.speed.set(savedSettings['speed']);
      }
    }
  }

  onSpeedSliderChange(event: ChangeContext): void {
    this.saveUserSettings();
    if (this.animationIsRunning()) {
      this.onPause();
      this.onPlay();
    }
  }

  onResetSettings(): void {
    this.speed.set(40);
    this.saveUserSettings();
    if (this.animationIsRunning()) {
      this.onPause();
      this.onPlay();
    }
  }
}
